import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import { usePermission } from '../hooks/usePermission';
import Modal from './common/Modal';
import { toast } from './common/Toast';
import * as usersApi from '../api/users';
import * as companiesApi from '../api/companies';
import BellDropdown from './BellDropdown';
import CompanySelectModal from './CompanySelectModal';
import PhoneChangeModal from './PhoneChangeModal';

const ROLE_LABEL: Record<string, string> = {
  super_admin: '최상위 관리자',
  admin: '관리자',
  manager: '매니저',
  user: '운전자',
};

const errMsg = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { error?: string } | undefined;
    if (data?.error) return data.error;
  }
  return fallback;
};

const Toolbar: React.FC = () => {
  const { me, role, logout, setMe, refreshMe } = useAuth();
  const { isStaff, isUser } = usePermission();

  const [nicknameOpen, setNicknameOpen] = useState(false);
  const [nicknameValue, setNicknameValue] = useState('');

  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });

  const [phoneOpen, setPhoneOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);

  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivatePassword, setDeactivatePassword] = useState('');

  const openNickname = () => {
    setNicknameValue(me?.nickname || '');
    setNicknameOpen(true);
  };

  const saveNickname = async () => {
    const trimmed = nicknameValue.trim();
    if (trimmed.length < 2 || trimmed.length > 20) {
      toast.error('닉네임은 2~20자여야 합니다');
      return;
    }
    try {
      const updated = await usersApi.changeMyNickname(trimmed);
      setMe(updated);
      toast.success('닉네임이 변경되었습니다');
      setNicknameOpen(false);
    } catch (err) {
      toast.error(errMsg(err, '닉네임 변경에 실패했습니다'));
    }
  };

  const savePassword = async () => {
    if (!pwForm.current || !pwForm.next) {
      toast.error('현재 비밀번호와 새 비밀번호를 입력해주세요');
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      toast.error('새 비밀번호가 서로 일치하지 않습니다');
      return;
    }
    try {
      await usersApi.changeMyPassword(pwForm.current, pwForm.next);
      sessionStorage.setItem('auth_redirect_message', '비밀번호가 변경되었습니다. 다시 로그인해주세요.');
      setPwOpen(false);
      setPwForm({ current: '', next: '', confirm: '' });
      logout();
      window.location.href = '/login';
    } catch (err) {
      toast.error(errMsg(err, '비밀번호 변경에 실패했습니다'));
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  const handleDeactivate = async () => {
    if (!deactivatePassword) {
      toast.error('비밀번호를 입력해주세요');
      return;
    }
    try {
      await usersApi.deactivateMe(deactivatePassword);
      setShowDeactivateModal(false);
      setDeactivatePassword('');
      logout();
      window.location.href = '/login';
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const data = err.response?.data as { error?: string; error_code?: string } | undefined;
        if (data?.error_code === 'WRONG_PASSWORD') {
          toast.error('비밀번호가 일치하지 않습니다');
          return;
        }
      }
      toast.error(errMsg(err, '계정 비활성화에 실패했습니다'));
    }
  };

  const submitCompanyChange = async (body: companiesApi.ChangeUserCompanyBody) => {
    await companiesApi.changeMyCompany(body);
    await refreshMe();
    toast.success('소속 회사가 변경되었습니다');
  };

  return (
    <div className="toolbar">
      <button type="button" className="toolbar__nickname" onClick={openNickname} title="닉네임 변경">
        <span className="toolbar__nickname-text">
          {me?.nickname && me.nickname !== me.phone
            ? me.nickname
            : (me?.name || me?.nickname || '…')}
        </span>
      </button>
      <span className="toolbar__role">{ROLE_LABEL[role || ''] || role || ''}</span>
      {isUser && me && (
        <button
          type="button"
          className={`toolbar__btn ${me.company_id === null ? 'toolbar__btn--warning' : ''}`}
          onClick={() => setCompanyOpen(true)}
          title={
            me.company_id === null
              ? '회사가 지정되지 않았습니다 (외부자)'
              : me.company_is_approved === false
              ? '회사 승인 대기중'
              : `소속: ${me.company_name || me.company || ''}`
          }
        >
          {me.company_id === null
            ? '회사 선택 (외부자)'
            : me.company_is_approved === false
            ? `${me.company_name || '...'} (대기중)`
            : `${me.company_name || me.company || '소속'}`}
        </button>
      )}
      {isStaff && <BellDropdown />}
      <button type="button" className="toolbar__btn" onClick={() => setPwOpen(true)}>
        비밀번호
      </button>
      <button type="button" className="toolbar__btn" onClick={() => setPhoneOpen(true)}>
        휴대폰
      </button>
      {!me?.is_default_super_admin && (
        <button
          type="button"
          className="toolbar__btn toolbar__btn--danger"
          onClick={() => setShowDeactivateModal(true)}
        >
          계정 비활성화
        </button>
      )}
      <button type="button" className="toolbar__logout" onClick={handleLogout}>
        로그아웃
      </button>

      <Modal
        isOpen={nicknameOpen}
        onClose={() => setNicknameOpen(false)}
        title="닉네임 변경"
        onConfirm={saveNickname}
        confirmText="저장"
      >
        <div className="form-group">
          <label>닉네임 (2~20자)</label>
          <input
            value={nicknameValue}
            onChange={(e) => setNicknameValue(e.target.value)}
            maxLength={20}
          />
        </div>
      </Modal>

      <Modal
        isOpen={pwOpen}
        onClose={() => setPwOpen(false)}
        title="비밀번호 변경"
        onConfirm={savePassword}
        confirmText="변경"
      >
        <div className="form-group">
          <label>현재 비밀번호</label>
          <input
            type="password"
            value={pwForm.current}
            onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>새 비밀번호</label>
          <input
            type="password"
            value={pwForm.next}
            onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>새 비밀번호 확인</label>
          <input
            type="password"
            value={pwForm.confirm}
            onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
          />
        </div>
        <p style={{ fontSize: 12, color: '#666' }}>
          8자 이상, 대·소문자, 숫자, 특수문자를 모두 포함해야 합니다.
        </p>
      </Modal>

      <PhoneChangeModal
        isOpen={phoneOpen}
        onClose={() => setPhoneOpen(false)}
        currentPhone={me?.phone || ''}
        onSuccess={refreshMe}
      />

      <CompanySelectModal
        isOpen={companyOpen}
        onClose={() => setCompanyOpen(false)}
        currentCompanyId={me?.company_id ?? null}
        onSubmit={submitCompanyChange}
        title="소속 회사 변경"
        submitLabel="변경"
      />

      <Modal
        isOpen={showDeactivateModal}
        onClose={() => { setShowDeactivateModal(false); setDeactivatePassword(''); }}
        title="계정 비활성화"
        onConfirm={handleDeactivate}
        confirmText="비활성화"
      >
        <p style={{ fontSize: 14, color: '#e53e3e', marginBottom: 16 }}>
          계정을 비활성화하면 다시 로그인할 수 없습니다.
          관리자에게 문의해야 재활성화할 수 있습니다.
        </p>
        <div className="form-group">
          <label>비밀번호 확인</label>
          <input
            type="password"
            value={deactivatePassword}
            onChange={(e) => setDeactivatePassword(e.target.value)}
            placeholder="현재 비밀번호를 입력하세요"
          />
        </div>
      </Modal>
    </div>
  );
};

export default Toolbar;
